import { FC, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { Badge } from "@vilocnv/allsetra-core";
import {
  Edit as EditIcon,
  CalendarToday as CalendarIcon
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import MileageCorrectionModal from "../MileageCorrectionModal";
import {
  MileageCorrectionCard,
  MileageCorrectionHeader,
  MileageCorrectionContent,
  MileageCorrectionItem,
  MileageCorrectionLabel,
  MileageCorrectionValue
} from "./MileageCorrectionDisplay.styled";
import { PathMileageIcon } from "assets/icons";
import { MuiButton } from "pages/dashboard/Reports/Reports.styled";
import { MileageCorrectionDisplayProps } from "./types";
import { useMileageCorrection } from "./hooks/useMileageCorrection";

const MileageCorrectionDisplay: FC<MileageCorrectionDisplayProps> = ({
  specificObject,
  accountId
}) => {
  const { t } = useTranslation(["translation", "formFieldsTranslation"]);
  const theme = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { correctionDate, lastReportedMileage, formatDate, formatMileage } =
    useMileageCorrection(specificObject);

  const handleEditClick = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <MileageCorrectionCard>
        <MileageCorrectionHeader>
          <Box>
            <Typography
              variant="h6"
              fontWeight={600}
              color={theme.palette.text.primary}
              sx={{ fontSize: "18px" }}
            >
              {t("typography.mileageCorrection")}
            </Typography>
            <Typography
              variant="body1"
              fontWeight={400}
              color={theme.palette.text.secondary}
              sx={{ fontSize: "14px", maxWidth: "400px" }}
            >
              {t("typography.mileageCorrectionDescription")}
            </Typography>
          </Box>

          <MuiButton
            id="create-new-group-button"
            aria-haspopup="true"
            onClick={handleEditClick}
            variant="text"
            sx={{ textTransform: "none", marginBottom: "24px" }}
          >
            {<EditIcon width={14} />}
            {t("typography.edit")}
          </MuiButton>
        </MileageCorrectionHeader>

        <MileageCorrectionContent>
          <MileageCorrectionItem>
            <MileageCorrectionLabel>
              {t("typography.correctionDate")}
            </MileageCorrectionLabel>
            <MileageCorrectionValue>
              <CalendarIcon sx={{ fontSize: "16px" }} />
              {formatDate(correctionDate)}
            </MileageCorrectionValue>
          </MileageCorrectionItem>

          <MileageCorrectionItem>
            <MileageCorrectionLabel>
              {t("typography.currentMileage")}
            </MileageCorrectionLabel>
            <MileageCorrectionValue>
              <Badge colorScheme={lastReportedMileage ? "grey" : "error"}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "#323946!important",
                    fontWeight: 600
                  }}
                >
                  <PathMileageIcon />
                  {formatMileage(lastReportedMileage)}
                </Box>
              </Badge>
            </MileageCorrectionValue>
          </MileageCorrectionItem>
        </MileageCorrectionContent>
      </MileageCorrectionCard>

      <MileageCorrectionModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        specificObject={specificObject}
        accountId={accountId}
      />
    </>
  );
};

export default MileageCorrectionDisplay;
